"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import JobsTable from "../jobstable/JobsTable";
import { useJobs } from "../../app/hooks/useJobs";
import { Pagination } from "../pagination/Pagination";
import { supabase } from "../../app/lib/supabase/client";

const HOME_PER_PAGE = 15;
/** Guest teaser: always 10 firms (must match API PREVIEW_MAX_JOBS). */
const HOME_PREVIEW_COUNT = 10;

export default function HomePage() {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("parsed_at");
  const [sortDir, setSortDir] = useState("desc");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!cancelled) {
        setIsLoggedIn(Boolean(session?.user));
        setAuthReady(true);
      }
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(Boolean(session?.user));
      setAuthReady(true);
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const usePreview = authReady && !isLoggedIn;

  const { jobs, total, previewShown, isLoading, isError } = useJobs({
    page: usePreview ? 1 : page,
    perPage: usePreview ? HOME_PREVIEW_COUNT : HOME_PER_PAGE,
    q: "",
    // Preview: sort only reorders the fixed 10 firms; API keeps the firm set stable.
    sortBy,
    sortDir,
    preview: usePreview,
    enabled: authReady,
  });

  const hiddenCount = usePreview ? Math.max(0, (total || 0) - (previewShown || jobs.length || 0)) : 0;

  return (
    <main role="main" className="w-full mt-4 mb-4 px-2 sm:px-4">
      {isError && !isLoading && <div className="text-center mt-4 text-red-500">Error loading jobs</div>}

      <div className="w-full overflow-auto border rounded-lg border-gray-200">
        <JobsTable
          data={jobs}
          isLoading={!authReady || isLoading}
          isError={isError}
          searchTerm=""
          sortBy={sortBy}
          sortDir={sortDir}
          skeletonRows={10}
          onSortChange={(col, dir) => {
            setSortBy(col);
            setSortDir(dir);
            setPage(1);
          }}
        />
      </div>

      {usePreview ? (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-[1rem] border border-[#E4E7EC] bg-[#FAFCFF] px-4 py-3">
          <p className="text-sm text-[#596379] leading-relaxed">
            Showing <span className="font-medium text-[#01194c]">1 role per company</span>
            {hiddenCount > 0 ? (
              <>
                {" "}
                — <span className="font-medium text-[#01194c]">{hiddenCount}+ more</span> after free signup
              </>
            ) : (
              <> — create a free account for the full job board</>
            )}
          </p>
          <Link
            href="/?register=1"
            className="inline-flex shrink-0 items-center justify-center px-3 py-1.5 text-[12px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-[3px] hover:bg-green-100 transition-colors"
          >
            Unlock full board
          </Link>
        </div>
      ) : (
        <Pagination page={page} total={total} perPage={HOME_PER_PAGE} isLoading={!authReady || isLoading} onChange={(p) => setPage(p)} />
      )}
    </main>
  );
}
